// From JavaScript, so the `@z/source` condition applies and the library's source styles are used; a
// CSS `@import` would look for its unbuilt `dist`.
import "@zsherman/editor/style.css";
import { createDocumentSession } from "@zsherman/editor/persistence";
import { Editor, type EditorHandle, type SelectionState } from "@zsherman/editor/react";
import {
  IconButton,
  Tooltip,
  cn,
  useShortcut,
  eventMatches,
  detectPlatform,
  shouldLeaveShortcutToBrowser,
} from "@z/ui";
import { FilePlus2, ListTodo } from "lucide-react";
import {
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { EditorTools, type LinkDraft } from "./EditorTools";
import { safeLink } from "./markdown";
import { createHostRepository, markdownOf, toStored } from "./repository";
import type { EditorContent, EditorValue, MarkdownEditorProps, MarkdownSelection } from "./types";
import { HQ_URL_POLICY } from "./urlPolicy";
export type {
  EditorContent,
  EditorValue,
  MarkdownEditorHandle,
  MarkdownEditorProps,
  MarkdownSelection,
  StoredEditorDocument,
} from "./types";

const initialState: SelectionState = {
  bold: false,
  italic: false,
  strikethrough: false,
  code: false,
  link: null,
  block: null,
  expanded: false,
  hasContent: false,
  canUndo: false,
  canRedo: false,
};

const emptySelection: MarkdownSelection = {
  text: "",
  line: "",
  markdown: "",
  blockIds: [],
  coords: null,
};

/** Whether a value from the host is what this editor last wrote or opened. */
function sameValue(a: EditorValue, b: EditorValue): boolean {
  if (a.markdown !== b.markdown) return false;
  if (a.document === undefined || b.document === undefined) return true;
  return a.document === b.document || JSON.stringify(a.document) === JSON.stringify(b.document);
}

function EditorShortcutScope({
  children,
  className,
  onAddTodo,
}: {
  children: ReactNode;
  className: string;
  onAddTodo: () => void;
}) {
  const shortcut = useShortcut("editor.addTodo");
  return (
    <div
      className={className}
      onKeyDownCapture={(event) => {
        if (
          shortcut &&
          eventMatches(event, shortcut, detectPlatform()) &&
          !event.nativeEvent.isComposing &&
          !event.repeat
        ) {
          event.preventDefault();
          event.stopPropagation();
          onAddTodo();
        } else if (shouldLeaveShortcutToBrowser(event, detectPlatform())) event.stopPropagation();
      }}
    >
      {children}
    </div>
  );
}

export function MarkdownEditor({
  value,
  documentId = "document",
  revision,
  onChange,
  onBlur,
  onAddTodo,
  onExtract,
  onNavigate,
  acceptExternalChange,
  autoFocus,
  toolbar = true,
  beforeContent,
  toolbarStart,
  toolbarEnd,
  contentClassName,
  label = "Note",
  placeholder = "Start writing…",
  className,
  ref,
}: MarkdownEditorProps) {
  const engine = useRef<EditorHandle>(null);
  // Fixed for the life of the editor, like its session: a daily note gains its id after its first
  // keystroke, and the editor that took that keystroke carries on.
  const [editorId] = useState(documentId);
  // The id saved documents carry: the latest, since a daily note gains one while it is open.
  const latestId = useRef(documentId);
  useLayoutEffect(() => {
    latestId.current = documentId;
  });
  // What this editor last opened or wrote: a value equal to it coming back is its own echo.
  const current = useRef<EditorValue>(value);
  const currentRevision = useRef(revision);
  const pendingText = useRef("");
  const [snapshot, setSnapshot] = useState(initialState);
  const [link, setLink] = useState<LinkDraft | null>(null);
  const callbacks = useRef({
    onChange,
    onBlur,
    onAddTodo,
    onExtract,
    onNavigate,
    acceptExternalChange,
  });
  useLayoutEffect(() => {
    callbacks.current = {
      onChange,
      onBlur,
      onAddTodo,
      onExtract,
      onNavigate,
      acceptExternalChange,
    };
  });

  // Whether a focus asked for before the editor was ready has been given. Only `autoFocus` and an
  // early `focus()` ask; once given, it is never given again.
  const focused = useRef(!autoFocus);
  // One session per mount: consumers key the editor on the document they show.
  const [{ session, repository }] = useState(() => {
    const store = createHostRepository(value);
    // No debounce here: the host's autosave debounces, and until it has the content it says so.
    const documentSession = createDocumentSession({
      documentId: editorId,
      repository: store,
      debounceMs: 0,
      maxWaitMs: 0,
    });
    return { session: documentSession, repository: store };
  });
  useLayoutEffect(() => {
    repository.publishTo(async (document) => {
      // Read in the same task as the snapshot the session just took, so the two agree. The last
      // edit before unmounting is saved after the editor is gone, and written without it.
      // Rejects once the editor is gone; the document the session saved is then the source.
      const exported = await engine.current?.exportMarkdown().catch(() => undefined);
      const markdown = exported?.text ?? markdownOf(document);
      const stored = toStored({ ...document, documentId: latestId.current });
      const content: EditorContent = { document: stored, markdown };
      current.current = content;
      callbacks.current.onChange(content, currentRevision.current);
    });
  }, [repository]);

  // Hosts pass `value` as a fresh object on every render. Only a change to what it holds is news:
  // re-running on a re-render would compare a stale store value (autosave has committed, the store
  // has not yet said so) with the newest draft, and reload the draft away.
  const { document: incomingDocument, markdown: incomingMarkdown } = value;
  useEffect(() => {
    const incoming: EditorValue = { document: incomingDocument, markdown: incomingMarkdown };
    if (sameValue(incoming, current.current)) {
      currentRevision.current = revision;
      return;
    }
    if (callbacks.current.acceptExternalChange?.() === false) return;
    current.current = incoming;
    currentRevision.current = revision;
    // Remote text must never be undoable into a stale draft: the reload clears history.
    repository.announce(incoming);
    void session.reload({ selection: "keep", history: "clear" });
  }, [incomingDocument, incomingMarkdown, revision, session, repository]);

  const content = async (): Promise<EditorContent | null> => {
    const handle = engine.current;
    const taken = handle?.getSnapshot();
    if (!handle || !taken?.ok) return null;
    const { text } = await handle.exportMarkdown();
    return {
      document: toStored({ ...taken.document, documentId: latestId.current }),
      markdown: text,
    };
  };
  const selection = (): MarkdownSelection => {
    const info = engine.current?.getSelectionInfo();
    if (!info) return emptySelection;
    return {
      text: info.text,
      line: info.line,
      markdown: info.markdown,
      blockIds: info.blockIds,
      coords: info.rect,
    };
  };
  const focus = () => {
    const applied = engine.current?.dispatch({ type: "focus" });
    // Asked before the document has loaded: focus it as soon as it has.
    if (applied?.status !== "applied") focused.current = false;
    return document.querySelector<HTMLElement>(`[data-editor-id="${editorId}"]`);
  };
  const insertText = (text: string) => {
    const handle = engine.current;
    if (!handle || handle.dispatch({ type: "focus" }).status !== "applied") {
      pendingText.current += text;
      return;
    }
    // Dictation and snippets arrive as Markdown: lists and checkboxes spoken become blocks.
    const result = handle.dispatch({ type: "insert-markdown", markdown: text });
    if (result.status !== "applied") handle.dispatch({ type: "insert-text", text });
  };
  const addTodo = () => callbacks.current.onAddTodo?.(selection());

  useImperativeHandle(ref, () => ({
    focus: () => void focus(),
    getDoc: () => current.current.markdown,
    getContent: content,
    insertText,
    getSelection: selection,
    replaceBlocks: async (blockIds, markdown) => {
      const handle = engine.current;
      if (!handle) return null;
      const result = handle.dispatch({
        type: "apply-edit",
        expectedEditSequence: handle.getEditSequence(),
        target: { kind: "blocks", blockIds },
        markdown,
      });
      return result.status === "applied" ? content() : null;
    },
    undo: () => void engine.current?.dispatch({ type: "undo" }),
    flush: async () => {
      await engine.current?.flush();
    },
    setGhostText: (text) => engine.current?.setGhostText(text),
  }));

  const format = (kind: string) => {
    const handle = engine.current;
    if (!handle) return;
    // Formatting acts on the selection: an editor nobody has been in yet gets one at its start.
    if (kind !== "undo" && kind !== "redo" && kind !== "link") handle.dispatch({ type: "focus" });
    const blocks = {
      paragraph: "paragraph",
      heading1: "h1",
      heading2: "h2",
      heading3: "h3",
      list: "bullet",
      ordered: "number",
      task: "check",
      quote: "quote",
      code: "code",
    } as const;
    if (kind === "undo" || kind === "redo") handle.dispatch({ type: kind });
    else if (kind === "**") handle.dispatch({ type: "format-text", format: "bold" });
    else if (kind === "*") handle.dispatch({ type: "format-text", format: "italic" });
    else if (kind === "`") handle.dispatch({ type: "format-text", format: "code" });
    else if (kind === "link") {
      setLink({
        url: snapshot.link ?? "https://",
        title: engine.current?.getSelectionInfo()?.text ?? "",
        selected: snapshot.expanded || snapshot.link !== null,
      });
      return;
    } else if (kind in blocks) {
      const block = blocks[kind as keyof typeof blocks];
      // The toolbar toggles: a list or quote again turns back into a paragraph.
      const toggleOff = snapshot.block === block && block !== "paragraph";
      handle.dispatch({ type: "set-block", block: toggleOff ? "paragraph" : block });
    }
    focus();
  };
  const active = new Set<string>();
  if (snapshot.bold) active.add("**");
  if (snapshot.italic) active.add("*");
  if (snapshot.code) active.add("`");
  if (snapshot.link) active.add("link");
  if (snapshot.block === "h2") active.add("heading2");
  if (snapshot.block === "bullet") active.add("list");
  if (snapshot.block === "check") active.add("task");

  return (
    <div
      data-markdown-editor=""
      data-editor-engine="z-editor"
      className={cn(
        "hq-markdown group/editor relative @container/editor flex min-h-0 min-w-0 flex-col text-fg",
        className,
      )}
    >
      <div
        data-sticky-editor-toolbar={toolbar ? "" : undefined}
        className={cn(
          toolbar
            ? "sticky top-0 z-10 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border/60 bg-bg/95 px-4 py-2 backdrop-blur-sm"
            : "flex min-h-10 items-center gap-3",
        )}
      >
        {toolbarStart && (
          <div className={toolbar ? "min-w-0 @max-[640px]/editor:w-full" : "min-w-0"}>
            {toolbarStart}
          </div>
        )}
        <EditorTools
          expanded={toolbar}
          active={active}
          canUndo={snapshot.canUndo}
          canRedo={snapshot.canRedo}
          focus={focus}
          onAddTodo={onAddTodo ? addTodo : undefined}
          canAddTodo={snapshot.hasContent}
          onFormat={format}
          link={link}
          onCloseLink={() => {
            setLink(null);
            focus();
          }}
          onSaveLink={(title, url) => {
            const handle = engine.current;
            if (handle && link) {
              if (link.selected) handle.dispatch({ type: "set-link", url });
              else if (url) {
                const markdown = `[${(title || url).replace(/[\\[\]]/g, "\\$&")}](${url})`;
                handle.dispatch({ type: "focus" });
                handle.dispatch({ type: "insert-markdown", markdown });
              }
            }
            setLink(null);
            focus();
          }}
        />
        {((toolbar && onAddTodo) || onExtract) && (
          <div className="flex shrink-0 gap-1">
            {toolbar && onAddTodo && (
              <Tooltip content="Add as task" shortcutId="editor.addTodo">
                <IconButton
                  label="Add as task"
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={addTodo}
                >
                  <ListTodo />
                </IconButton>
              </Tooltip>
            )}
            {onExtract && (
              <Tooltip content="Move selection to a new note">
                <IconButton
                  label="Move selection to a new note"
                  size="sm"
                  disabled={!snapshot.expanded}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => callbacks.current.onExtract?.(selection())}
                >
                  <FilePlus2 />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
        {toolbarEnd}
      </div>
      {beforeContent}
      <EditorShortcutScope
        className={cn("min-w-0 flex-1", toolbar && "text-[17px]", contentClassName)}
        onAddTodo={addTodo}
      >
        <div
          className="min-h-24"
          onBlur={(event) => {
            // Focus moving within the editor (its menus, its toolbar) is not leaving it.
            if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
            void (engine.current?.flush() ?? Promise.resolve()).then(() =>
              callbacks.current.onBlur?.(),
            );
          }}
        >
          <Editor
            ref={(handle) => {
              engine.current = handle;
              if (!handle) return;
              if (pendingText.current) {
                const text = pendingText.current;
                pendingText.current = "";
                insertText(text);
              }
            }}
            session={session}
            preset={toolbar ? "document" : "description"}
            // Handles take a gutter; a compact surface keeps its text flush with its heading.
            blockHandles={toolbar}
            tables={false}
            urlPolicy={HQ_URL_POLICY}
            labels={{ editor: label, placeholder }}
            editableProps={{
              "data-writing-surface": "",
              "data-editor-id": editorId,
              "aria-multiline": "true",
            }}
            onSelectionChange={(state) => {
              setSnapshot(state);
              // The first report means the editor is ready.
              if (!focused.current) {
                focused.current = true;
                engine.current?.dispatch({ type: "focus", at: "end" });
              }
            }}
            onLinkActivate={(href) => {
              const url = safeLink(href);
              return url !== null && callbacks.current.onNavigate?.(url) === true;
            }}
          />
        </div>
      </EditorShortcutScope>
    </div>
  );
}
